import React from 'react';
import { Card, Button, Table } from 'react-bootstrap';
import {
  FiMapPin, FiPhone, FiMail, FiExternalLink, FiClock,
} from 'react-icons/fi';
import { FaWhatsapp, FaFacebook, FaInstagram } from 'react-icons/fa';
import type { IconBaseProps } from 'react-icons';
import { TbDeviceLandlinePhone } from "react-icons/tb";

export interface LocationProps {
  slug: string;
  name: string;
  address: string;
  phoneDisplay: string;
  landlineDisplay: string;
  landlineTel: string;
  phoneTel: string;
  email?: string;
  whatsappUrl: string;
  facebookUrl: string;
  instagramUrl: string;
  mapsEmbedQuery: string;
  opening: { day: string; open: string; close: string }[];
}

/* ---------- Icon wrappers (fix JSX typing issues) ---------- */
const MapPinIcon:   React.FC<IconBaseProps> = (p) => React.createElement(FiMapPin as any, p);
const PhoneIcon:    React.FC<IconBaseProps> = (p) => React.createElement(FiPhone as any, p);
const LandLinePhoneIcon:    React.FC<IconBaseProps> = (p) => React.createElement(TbDeviceLandlinePhone as any, p);
const MailIcon:     React.FC<IconBaseProps> = (p) => React.createElement(FiMail as any, p);
const ExtLinkIcon:  React.FC<IconBaseProps> = (p) => React.createElement(FiExternalLink as any, p);
const ClockIcon:    React.FC<IconBaseProps> = (p) => React.createElement(FiClock as any, p);

const WhatsappIcon: React.FC<IconBaseProps> = (p) => React.createElement(FaWhatsapp as any, p);
const FacebookIcon: React.FC<IconBaseProps> = (p) => React.createElement(FaFacebook as any, p);
const InstagramIcon:React.FC<IconBaseProps> = (p) => React.createElement(FaInstagram as any, p);

const LocationCard: React.FC<LocationProps> = (props) => {
  const {
    name, address, phoneTel, email,
    landlineDisplay, phoneDisplay, landlineTel,
    whatsappUrl, facebookUrl, instagramUrl, mapsEmbedQuery,
    opening, 
  } = props;

  const mapsEmbedSrc = `https://www.google.com/maps?q=${encodeURIComponent(mapsEmbedQuery)}&output=embed`;
  const landlineExists: boolean = Boolean(landlineDisplay && landlineTel);
  const phoneExists: boolean    = Boolean(phoneDisplay && phoneTel);

  return (
    <Card className="h-100 shadow-sm">
      <Card.Body>
        <Card.Title className="d-flex align-items-center justify-content-between">
          <span className="fw-semibold">{name}</span>
          <span className="text-muted small d-none d-md-inline">Location</span>
        </Card.Title>

        <div className="d-flex align-items-start mb-3">
          <MapPinIcon className="me-2 mt-1 flex-shrink-0" />
          <address className="mb-0">{address}</address>
        </div>

        {/* Map */}
        <div className="ratio ratio-4x3 rounded overflow-hidden mb-3">
          <iframe
            title={`${name} Map`}
            src={mapsEmbedSrc}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        {/* Contacts */}
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button as="a" href={`tel:${phoneTel}`} variant="primary" aria-label={`Call ${name}`}>
            <PhoneIcon className="me-2" /> Call Mobile
          </Button>

          <Button as="a" href={`tel:${phoneTel}`} variant="secondary" aria-label={`Call ${name}`}>
            <LandLinePhoneIcon className="me-2" /> Call Landline
          </Button>

          <Button as="a" href={whatsappUrl} target="_blank" rel="noopener noreferrer" variant="success" aria-label={`WhatsApp ${name}`}>
            <WhatsappIcon className="me-2" /> WhatsApp
          </Button>

          <Button as="a" href={facebookUrl} target="_blank" rel="noopener noreferrer" variant="outline-primary" aria-label={`Facebook ${name}`}>
            <FacebookIcon className="me-2" /> Facebook
          </Button>

          <Button as="a" href={instagramUrl} target="_blank" rel="noopener noreferrer" variant="outline-dark" aria-label={`Instagram ${name}`}>
            <InstagramIcon className="me-2" /> Instagram
          </Button>

          {email && (
            <Button as="a" href={`mailto:${email}`} variant="outline-secondary" aria-label={`Email ${name}`}>
              <MailIcon className="me-2" /> Email
            </Button>
          )}

          <Button
            as="a"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsEmbedQuery)}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="outline-secondary"
            aria-label={`Directions to ${name}`}
          >
            <ExtLinkIcon className="me-2" /> Get Directions
          </Button>
        </div>

        {
          (
            phoneExists
            ||
            landlineExists
          ) &&
        (<div className="rounded-2xl border border-slate-200 bg-white/70 backdrop-blur p-4 sm:p-5 shadow-sm dark:bg-slate-900/60 dark:border-slate-800">
          <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Contact Phone numbers</h4>

          <div className="grid gap-4 sm:grid-cols-2">
            {/* Mobile */}
            {phoneExists && (<div className="flex items-center gap-3">
              <span className="inline-flex size-10 items-center justify-center rounded-xl border border-lime-200 bg-lime-50 text-lime-600 dark:border-lime-900/40 dark:bg-lime-900/20">
                {/* phone icon */}
                <PhoneIcon/>
              </span>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Phone</div>
                <a
                  href={phoneTel}
                  className="block font-medium text-slate-800 hover:text-lime-600 transition truncate dark:text-slate-100"
                  aria-label={`Call ${phoneDisplay}`}
                >
                  {phoneDisplay}
                </a>
              </div>
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(phoneDisplay ?? phoneTel)}
                className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                title="Copy number"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="size-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H8a2 2 0 0 0-2 2v2h2V3h8v8h2V3a2 2 0 0 0-2-2z"/><path d="M5 7h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zm0 2v10h10V9H5z"/></svg>
                Copy
              </button>
            </div>)}

            {/* Landline */}
            { landlineExists && (<div className="flex items-center gap-3">
              <span className="inline-flex size-10 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-600 dark:border-blue-900/40 dark:bg-blue-900/20">
                {/* landline icon */}
                <LandLinePhoneIcon/>
              </span>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Landline</div>
                <a
                  href={landlineTel}
                  className="block font-medium text-slate-800 hover:text-blue-600 transition truncate dark:text-slate-100"
                  aria-label={`Call ${landlineDisplay}`}
                >
                  {landlineDisplay}
                </a>
              </div>
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(landlineDisplay ?? landlineTel)}
                className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                title="Copy number"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="size-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H8a2 2 0 0 0-2 2v2h2V3h8v8h2V3a2 2 0 0 0-2-2z"/><path d="M5 7h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zm0 2v10h10V9H5z"/></svg>
                Copy
              </button>
            </div>)}
          </div>
        </div>)}


        {/* Hours & Costs */}
        <div className="d-flex flex-column flex-lg-row gap-3">
          <div className="flex-fill">
            <div className="d-flex align-items-center mb-2">
              <ClockIcon className="me-2" /> <strong className="me-1">Opening Times</strong>
            </div>
            <Table size="sm" className="mb-0 align-middle">
              <tbody>
                {opening.map((o) => (
                  <tr key={`${name}-${o.day}`}>
                    <td className="text-muted">{o.day}</td>
                    <td className="text-end">
                      {o.open} – {o.close}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default LocationCard;
